import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const ease = [0.22, 1, 0.36, 1];

function Line({ children, i, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease, delay: 0.15 + i * 0.12 }}>
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { t, to } = useLang();
  const h = t.home.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="relative overflow-hidden bg-[#F8FAFC] pb-20 pt-32 sm:pt-40 lg:pb-32" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-32 top-10 h-[520px] w-[520px] rounded-full bg-teal-200/50 blur-[120px]" />
        <div className="absolute -right-24 top-40 h-[420px] w-[420px] rounded-full bg-sky-200/60 blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      </motion.div>

      <div className="container-x relative grid items-center gap-14 lg:grid-cols-12">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
            <Sparkles size={14} /> {h.eyebrow}
          </motion.p>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.02] text-slate-900 sm:text-5xl lg:text-6xl xl:text-[4.5rem]" data-testid="hero-title">
            {h.lines.map((l, i) => <Line key={i} i={i}>{l}</Line>)}
            <Line i={h.lines.length} className="bg-gradient-to-r from-sky-600 via-teal-600 to-emerald-600 bg-clip-text font-semibold italic text-transparent">{h.accent}</Line>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.7 }} className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg" data-testid="hero-subtitle">
            {h.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.85 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to={to("contact")} className="btn-primary" data-testid="hero-primary-cta">{h.cta1} <ArrowRight size={16} /></Link>
            <Link to={`${to("contact")}#contact-forms`} className="btn-secondary" data-testid="hero-secondary-cta">{h.cta2}</Link>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }} className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-600" data-testid="hero-badges">
            {h.badges.map((b) => (
              <li key={b} className="flex items-center gap-2"><Check size={14} className="text-teal-600" />{b}</li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div style={{ y: yCard, rotateX: rx, rotateY: ry, transformPerspective: 1200 }} className="relative lg:col-span-5" data-testid="hero-card">
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1, ease, delay: 0.5 }} className="relative rounded-[2rem] bg-navy p-8 text-white shadow-[0_40px_80px_-40px_rgba(15,23,42,0.6)] sm:p-10">
            <div className="grain absolute inset-0 rounded-[2rem]" />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-300">{h.cardTitle}</p>
              <ol className="mt-6 space-y-4">
                {h.cardSteps.map((s, i) => (
                  <motion.li key={s} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease, delay: 0.9 + i * 0.12 }} className="flex items-center gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 font-mono text-xs text-teal-300">0{i + 1}</span>
                    <span className="font-display text-base font-medium sm:text-lg">{s}</span>
                  </motion.li>
                ))}
              </ol>
              <p className="mt-8 border-t border-white/10 pt-6 text-sm text-slate-400">{h.cardNote}</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease, delay: 1.3 }} className="animate-float absolute -left-6 -top-6 hidden rounded-2xl border border-white bg-white/90 px-4 py-3 shadow-xl backdrop-blur sm:block">
            <p className="text-xs text-slate-500">Bagnolet · Île-de-France</p>
            <p className="font-display text-sm font-semibold text-slate-900">{h.badges[1]}</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
